/**
 * A FELHASZNÁLÓ ŰRLAPJA — felvétel ÉS hozzáférés, EGY komponensben.
 *
 * Egy, és nem kettő, mert a hatókör és a kiosztás szabálya a kettőben azonos
 * — a backenden is (`CreateUserDto extends SetUserAccessDto`). Két komponens
 * azt a hibát hozná be, hogy egy új szabály az egyikben megjelenik, a
 * másikban nem.
 *
 * ═══ AMI SZÁNDÉKOSAN NINCS RAJTA ═══
 *
 *   • A JELSZÓ. Az új kolléga varázslinket kap, és maga állít jelszót (Viktor,
 *     2026-09-11) — az admin nem lát és nem továbbít belépést érő titkot.
 *   • A NÉV, AZ E-MAIL ÉS A SZEREPKÖR SZERKESZTÉSE meglévő felhasználónál. A
 *     szerepkör-váltás egy másik ember, nem egy beállítás; az e-mail cím a
 *     belépés azonosítója.
 *   • AZ ÜGYFÉL SZEREPKÖR — ügyfél-felhasználót ezen az úton nem lehet
 *     felvenni (`CreateUserDto`).
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@/components/common';
import type { CompanyRow } from './useCompanies';
import type {
  AccessInput,
  AssignableScope,
  OfficeRole,
  UserRow,
  UsersState,
  UsersStatus,
} from './useUsers';

/**
 * A HATÓKÖR ÉS A KIJELÖLÉS → A KÉRÉS TÖRZSE. Tiszta függvény.
 *
 * ⚠️ „AZ IRODA MINDEN CÉGE" MELLETT ÜRES LISTA MEGY, akkor is, ha előtte cégek
 * voltak kijelölve. A szerver a nem üres listát 400-zal utasítja el — és ha
 * elfogadná, egy későbbi „kiosztott cégek"-re váltás a RÉGI, rég elfelejtett
 * kijelölést kapcsolná vissza.
 */
export function toAccessInput(scope: AssignableScope, selected: ReadonlySet<string>): AccessInput {
  return { scope, companyIds: scope === 'ASSIGNED' ? [...selected].sort() : [] };
}

export interface UserFormProps {
  /** `null` felvételnél; meglévő felhasználónál csak a hozzáférés szerkeszthető. */
  user: UserRow | null;
  state: UsersState;
  /** A szelet zárója — a hibát is törli, és az `Escape` is ezt hívja (`CompanyForm`). */
  onDone: () => void;
}

export function UserForm({ user, state, onDone }: UserFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OfficeRole>('ACCOUNTANT');
  const [scope, setScope] = useState<AssignableScope>(
    user?.scope === 'ASSIGNED' ? 'ASSIGNED' : 'ALL_COMPANIES'
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(user?.companyIds ?? []));

  /**
   * A horog állapota MEGOSZTOTT a lista és az űrlap között: egy korábbi sor-
   * művelet hibája nem jelenhet meg egy frissen megnyitott űrlapon.
   */
  const [submitted, setSubmitted] = useState(false);
  const busy = state.status === 'saving';

  function toggleCompany(companyId: string) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);

    const access = toAccessInput(scope, selected);
    if (user) {
      state.setAccess(user.id, access, onDone);
    } else {
      state.create({ name: name.trim(), email: email.trim(), role, ...access }, onDone);
    }
  }

  return (
    <form className="card settings__form" onSubmit={onSubmit}>
      <h2>
        {user ? t('users.form.accessTitle', { name: user.name }) : t('users.form.createTitle')}
      </h2>

      <FormError status={submitted ? state.status : null} detail={state.detail} />

      {user ? (
        <p className="subtitle">{user.email}</p>
      ) : (
        <IdentityFields
          name={name}
          email={email}
          role={role}
          onName={setName}
          onEmail={setEmail}
          onRole={setRole}
        />
      )}

      <ScopeFields
        scope={scope}
        selected={selected}
        companies={state.companies}
        onScope={setScope}
        onToggle={toggleCompany}
      />

      <div className="settings__toolbar">
        <button type="submit" className="settings__primary" disabled={busy}>
          {busy ? t('users.form.saving') : user ? t('users.form.save') : t('users.form.create')}
        </button>
        <button type="button" onClick={onDone} disabled={busy}>
          {t('users.form.cancel')}
        </button>
      </div>
    </form>
  );
}

function IdentityFields({
  name,
  email,
  role,
  onName,
  onEmail,
  onRole,
}: {
  name: string;
  email: string;
  role: OfficeRole;
  onName: (value: string) => void;
  onEmail: (value: string) => void;
  onRole: (value: OfficeRole) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <label htmlFor="user-name">{t('users.form.name')}</label>
      <input
        id="user-name"
        required
        // A billentyűzet-első felület a fókusszal kezd, nem az egérrel.
        // biome-ignore lint/a11y/noAutofocus: az űrlap a képernyő egyetlen tartalma
        autoFocus
        value={name}
        onChange={(event) => onName(event.target.value)}
      />

      <label htmlFor="user-email">{t('users.form.email')}</label>
      <input
        id="user-email"
        type="email"
        required
        aria-describedby="user-email-hint"
        value={email}
        onChange={(event) => onEmail(event.target.value)}
      />
      <p id="user-email-hint" className="hint">
        {t('users.form.emailHint')}
      </p>

      {/* A `<label htmlFor>` a combobox GOMBJÁRA mutat (az viszi az `id`-t),
          tehát a kattintás odaviszi a fókuszt, és a képernyőolvasó is a
          mezőnévvel mondja be. */}
      <label htmlFor="user-role">{t('users.form.role')}</label>
      <Select
        id="user-role"
        value={role}
        options={[
          { value: 'ACCOUNTANT', label: t('user.role.ACCOUNTANT') },
          { value: 'ADMIN', label: t('user.role.ADMIN') },
        ]}
        onChange={onRole}
      />
    </>
  );
}

function ScopeFields({
  scope,
  selected,
  companies,
  onScope,
  onToggle,
}: {
  scope: AssignableScope;
  selected: ReadonlySet<string>;
  companies: CompanyRow[];
  onScope: (value: AssignableScope) => void;
  onToggle: (companyId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <fieldset>
        <legend>{t('users.form.scope')}</legend>
        <label className="settings__toggle">
          <input
            type="radio"
            name="user-scope"
            checked={scope === 'ALL_COMPANIES'}
            onChange={() => onScope('ALL_COMPANIES')}
          />
          {t('user.scope.ALL_COMPANIES')}
        </label>
        <label className="settings__toggle">
          <input
            type="radio"
            name="user-scope"
            checked={scope === 'ASSIGNED'}
            onChange={() => onScope('ASSIGNED')}
          />
          {t('user.scope.ASSIGNED')}
        </label>
      </fieldset>

      {scope === 'ASSIGNED' ? (
        <fieldset>
          <legend>{t('users.form.companies')}</legend>
          {companies.length === 0 ? <p className="hint">{t('users.form.noCompanies')}</p> : null}
          {companies.map((company) => (
            <label key={company.id} className="settings__toggle">
              <input
                type="checkbox"
                checked={selected.has(company.id)}
                onChange={() => onToggle(company.id)}
              />
              {/* AZ INAKTÍV CÉG IS KIOSZTHATÓ — a szerver is engedi, mert a
                  visszakapcsolt cég különben gazdátlanul térne vissza. */}
              {company.active ? company.name : `${company.name} (${t('companies.inactive')})`}
            </label>
          ))}
          {selected.size === 0 ? <p className="hint">{t('users.form.noneAssignedHint')}</p> : null}
        </fieldset>
      ) : null}
    </>
  );
}

/**
 * A MENTÉS HIBÁJA. A 409 és a 403 a SZERVER SZÖVEGÉT mutatja, szó szerint —
 * „ezzel az e-mail címmel már van felhasználó", „a jogosultságod közben
 * megváltozott".
 */
function FormError({ status, detail }: { status: UsersStatus | null; detail: string }) {
  const { t } = useTranslation();

  if (status === 'conflict' || status === 'refused') {
    return (
      <p className="error" role="alert">
        {detail}
      </p>
    );
  }
  if (status === 'invalid') {
    return (
      <p className="error" role="alert">
        {t('users.form.invalid', { detail })}
      </p>
    );
  }
  if (status === 'failed') {
    return (
      <p className="error" role="alert">
        {t('users.form.failed')}
      </p>
    );
  }
  return null;
}
